import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart, Bar, ResponsiveContainer, Cell, Tooltip, XAxis } from "recharts";
import { doc, setDoc, serverTimestamp, collection, query, orderBy, limit, onSnapshot, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useBooking, NEXT_DAY_SLOTS, cleanupOrphanedLocks } from "../context/BookingContext";
import { usePricing, PRICING_DEFAULTS } from "../context/PricingContext";
import { usePromo, PROMO_DEFAULTS, PROMO_TYPES, VALUE_LABELS } from "../context/PromoContext";
import { logAdminAction } from "../utils/adminLogger";

// ── Partner Codes Tab ──────────────────────────────────────────────────────────

const PARTNER_CODE_LIST = ["SABER15", "ASLAM15", "TAYYAB15"];

function PartnerCodesTab({ bookings }) {
  const [dateFilter, setDateFilter] = useState("All Time");
  const todayStr = new Date().toISOString().split("T")[0];

  const filtered = bookings.filter((b) => {
    if (!b.partnerCode) return false;
    const bDate = new Date(b.date + "T00:00:00");
    const now = new Date();
    if (dateFilter === "Today") return b.date === todayStr;
    if (dateFilter === "This Week") {
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay());
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return bDate >= start && bDate <= end;
    }
    if (dateFilter === "This Month") {
      return bDate.getMonth() === now.getMonth() && bDate.getFullYear() === now.getFullYear();
    }
    return true;
  });

  const ACTIVE = ["confirmed", "completed", "upcoming"];

  const codeStats = PARTNER_CODE_LIST.map((code) => {
    const uses = filtered.filter((b) => b.partnerCode === code);
    const revenue = uses.filter((b) => ACTIVE.includes(b.status)).reduce((s, b) => s + (b.total || 0), 0);
    const saved   = uses.filter((b) => ACTIVE.includes(b.status)).reduce((s, b) => s + (b.partnerDiscount || 0), 0);
    return { code, count: uses.length, revenue, saved };
  });

  const totalUses = codeStats.reduce((s, c) => s + c.count, 0);

  return (
    <div>
      {/* Date filter */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-1 p-1 rounded-xl border border-black/8 w-fit" style={{ background: "#f9fafb" }}>
          {["Today", "This Week", "This Month", "All Time"].map((f) => (
            <button
              key={f}
              onClick={() => setDateFilter(f)}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                dateFilter === f ? "bg-white text-black shadow-sm border border-black/6" : "text-black/40 hover:text-black"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        {totalUses > 0 && (
          <span className="text-black/40 text-xs font-medium">{totalUses} use{totalUses !== 1 ? "s" : ""} in period</span>
        )}
      </div>

      {/* Code cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {codeStats.map(({ code, count, revenue, saved }) => (
          <div key={code} className="rounded-2xl border border-black/8 p-5" style={{ background: "#f9fafb" }}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-black text-sm tracking-wider text-black">{code}</span>
              <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-purple-50 border border-purple-100 text-purple-600 tracking-wider">
                15% OFF
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-black/40">Bookings</span>
                <span className="text-black font-bold">{count}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-black/40">Revenue</span>
                <span className="text-black font-bold">₹{revenue.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-black/40">Discount Given</span>
                <span className="text-purple-600 font-bold">–₹{saved.toLocaleString()}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bookings list */}
      {filtered.length > 0 ? (
        <div className="rounded-2xl border border-black/8 overflow-hidden">
          <div className="px-5 py-3 border-b border-black/6" style={{ background: "#f9fafb" }}>
            <p className="text-xs font-black text-black/40 uppercase tracking-[0.2em]">Bookings with Partner Code</p>
          </div>
          <div className="divide-y divide-black/6">
            {filtered.map((b) => (
              <div key={b.id} className="px-5 py-3.5 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-black font-bold text-sm">{b.name}</p>
                  <p className="text-black/40 text-xs">{b.date} · {b.sport}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-purple-50 border border-purple-100 text-purple-600">
                    {b.partnerCode}
                  </span>
                  {b.partnerDiscount > 0 && (
                    <span className="text-purple-600 text-xs font-bold">–₹{b.partnerDiscount.toLocaleString()}</span>
                  )}
                  <span className="text-black font-bold text-sm">₹{(b.total || 0).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-black/8 py-12 text-center" style={{ background: "#f9fafb" }}>
          <p className="text-black/30 text-sm">No partner code bookings in this period</p>
        </div>
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatTime12(t) {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${period}`;
}

// Maps midnight-wrap slots to sortable hour values (00→24, 01→25, ..., 04→28)
function slotHour(t) {
  const h = parseInt(t, 10);
  return h < 5 ? h + 24 : h;
}

// Returns "HH:00" for the hour after a given slot
function slotEndTime(t) {
  const h = parseInt(t, 10);
  return `${String((h + 1) % 24).padStart(2, "0")}:00`;
}

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parseDevice(ua) {
  if (!ua) return null;
  const s = ua.toLowerCase();
  const deviceType =
    /tablet|ipad/.test(s) ? "Tablet" :
    /mobile|android|iphone/.test(s) ? "Mobile" : "Desktop";
  const browser =
    /edg\//.test(s)     ? "Edge" :
    /opr\/|opera/.test(s) ? "Opera" :
    /chrome\//.test(s)  ? "Chrome" :
    /firefox\//.test(s) ? "Firefox" :
    /safari\//.test(s)  ? "Safari" : "Browser";
  const os =
    /iphone|ipod/.test(s) ? "iOS" :
    /ipad/.test(s)         ? "iPadOS" :
    /android/.test(s)      ? "Android" :
    /windows/.test(s)      ? "Windows" :
    /mac os x/.test(s)     ? "macOS" :
    /linux/.test(s)        ? "Linux" : "Unknown";
  return { deviceType, browser, os };
}
const TOTAL_SLOTS_PER_DAY = 24; // 05:00–04:00 in 1-hr increments (19 regular + 5 next-day)

const STATUS_STYLES = {
  upcoming:  { badge: "bg-blue-50 text-blue-600 border border-blue-100" },
  confirmed: { badge: "bg-green-50 text-green-600 border border-green-100" },
  completed: { badge: "bg-green-50 text-green-600 border border-green-100" },
  pending:   { badge: "bg-amber-50 text-amber-600 border border-amber-100" },
  failed:    { badge: "bg-red-50 text-red-500 border border-red-100" },
  cancelled: { badge: "bg-red-50 text-red-500 border border-red-100" },
};

const SPORTS = ["Oval Cricket", "Box Cricket", "Pickleball"];
const REASONS = ["Maintenance", "Private Event", "Rain", "Other"];
const BLOCK_COURTS = [
  "All Courts",
  "Oval Cricket",
  "Box Cricket Court 1",
  "Box Cricket Court 2",
  "Pickleball Court 1",
  "Pickleball Court 2",
];

const SLOT_OPTIONS = [
  "05:00","06:00","07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00",
  "15:00","16:00","17:00","18:00","19:00","20:00","21:00","22:00","23:00",
  "00:00","01:00","02:00",
];

// Courts tab config — gold / blue / green
const COURTS_CONFIG = [
  {
    sport: "Oval Cricket",
    emoji: "🏏",
    tag: "1 Ground",
    courts: ["Oval Ground"],
    accent: "#b45309",
    accentLight: "#f59e0b",
    barFill: "#f59e0b",
    pillBg: "#fffbeb",
    pillBorder: "#fde68a",
    pillText: "#92400e",
  },
  {
    sport: "Box Cricket",
    emoji: "🏏",
    tag: "2 Courts",
    courts: ["Court 1", "Court 2"],
    accent: "#1d4ed8",
    accentLight: "#3b82f6",
    barFill: "#3b82f6",
    pillBg: "#eff6ff",
    pillBorder: "#bfdbfe",
    pillText: "#1e40af",
  },
  {
    sport: "Pickleball",
    emoji: "🏓",
    tag: "2 Courts",
    courts: ["Court 1", "Court 2"],
    accent: "#15803d",
    accentLight: "#22c55e",
    barFill: "#22c55e",
    pillBg: "#f0fdf4",
    pillBorder: "#bbf7d0",
    pillText: "#166534",
  },
];

// ── Toast ──────────────────────────────────────────────────────────────────────

function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2.5 bg-green-600 text-white text-sm font-semibold px-5 py-3 rounded-2xl shadow-xl"
    >
      <span>✓</span>
      {message}
    </motion.div>
  );
}

// ── Add Booking Modal ──────────────────────────────────────────────────────────

function AddBookingModal({ onClose, onSave }) {
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    name: "", phone: "", sport: "Oval Cricket",
    date: today, slot: "06:00", paymentStatus: "Paid",
  });
  const [errors, setErrors] = useState({});

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!/^[6-9]\d{9}$/.test(form.phone)) e.phone = "Enter valid 10-digit number";
    if (!form.date) e.date = "Required";
    return e;
  };

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    const id = "BK" + Date.now().toString().slice(-4);
    onSave({
      id,
      name: form.name.trim(),
      phone: form.phone.trim(),
      sport: form.sport,
      date: form.date,
      slots: [form.slot],
      total: form.paymentStatus === "Paid" ? 800 : 0,
      status: "upcoming",
      paymentStatus: form.paymentStatus,
      source: "manual",
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.22 }}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-black font-black text-lg">Add Booking</h2>
          <button onClick={onClose} className="text-black/30 hover:text-black text-xl leading-none">×</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-black font-bold text-sm mb-1.5">Customer Name</label>
            <input
              type="text"
              placeholder="Virat Kohli"
              className={`w-full border rounded-xl px-4 py-2.5 text-sm text-black bg-black/[0.03] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/40 focus:border-[#f59e0b]/60 transition-all ${errors.name ? "border-red-300" : "border-black/10"}`}
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-black font-bold text-sm mb-1.5">Phone Number</label>
            <input
              type="tel"
              placeholder="9876543210"
              className={`w-full border rounded-xl px-4 py-2.5 text-sm text-black bg-black/[0.03] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/40 focus:border-[#f59e0b]/60 transition-all ${errors.phone ? "border-red-300" : "border-black/10"}`}
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label className="block text-black font-bold text-sm mb-1.5">Sport</label>
            <select
              className="w-full border border-black/10 rounded-xl px-4 py-2.5 text-sm text-black bg-black/[0.03] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/40 transition-all"
              value={form.sport}
              onChange={(e) => set("sport", e.target.value)}
            >
              {SPORTS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-black font-bold text-sm mb-1.5">Date</label>
              <input
                type="date"
                min={today}
                className={`w-full border rounded-xl px-3 py-2.5 text-sm text-black bg-black/[0.03] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/40 transition-all ${errors.date ? "border-red-300" : "border-black/10"}`}
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
              />
              {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
            </div>
            <div>
              <label className="block text-black font-bold text-sm mb-1.5">Time Slot</label>
              <select
                className="w-full border border-black/10 rounded-xl px-3 py-2.5 text-sm text-black bg-black/[0.03] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/40 transition-all"
                value={form.slot}
                onChange={(e) => set("slot", e.target.value)}
              >
                {SLOT_OPTIONS.map((s) => <option key={s}>{formatTime12(s)}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-black font-bold text-sm mb-1.5">Payment Status</label>
            <div className="flex gap-3">
              {["Paid", "Unpaid"].map((ps) => (
                <button
                  key={ps}
                  type="button"
                  onClick={() => set("paymentStatus", ps)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold border transition-all ${
                    form.paymentStatus === ps
                      ? ps === "Paid"
                        ? "bg-green-50 border-green-300 text-green-700"
                        : "bg-red-50 border-red-300 text-red-600"
                      : "bg-white border-black/10 text-black/40"
                  }`}
                >
                  {ps === "Paid" ? "✓ Paid" : "✗ Unpaid"}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-black/10 text-black/50 text-sm font-semibold hover:bg-black/[0.03] transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-3 rounded-xl bg-[#f59e0b] hover:bg-amber-400 text-black font-black text-sm transition-all"
          >
            Save Booking
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Cancel Booking Modal ───────────────────────────────────────────────────────

const REFUND_OPTIONS = [
  { value: "process_now",    label: "Process Refund Now — via Razorpay API" },
  { value: "refund_pending", label: "Refund Pending — I'll process manually in Razorpay" },
  { value: "no_refund",      label: "No Refund — customer owes nothing back" },
];

function CancelModal({ booking, onClose, onConfirm }) {
  const hasPaymentId = !!booking.razorpay_payment_id;
  const [refundStatus, setRefundStatus] = useState(hasPaymentId ? "process_now" : "no_refund");
  const [notes, setNotes] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [apiError, setApiError] = useState(null);

  const sorted    = [...(booking.slots || [])].sort();
  const dateLabel = new Date(booking.date).toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const handleConfirm = async () => {
    setConfirming(true);
    setApiError(null);
    await onConfirm(refundStatus, notes.trim(), (err) => {
      setApiError(err);
      setConfirming(false);
    });
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.22 }}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-full bg-red-50 border border-red-100 flex items-center justify-center text-sm">🚫</span>
            <h2 className="text-black font-black text-lg">Cancel Booking</h2>
          </div>
          <button onClick={onClose} className="text-black/30 hover:text-black text-xl leading-none">×</button>
        </div>

        {/* Booking summary */}
        <div className="rounded-xl border border-black/8 p-4 mb-5" style={{ background: "#f9fafb" }}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-black font-bold text-sm leading-tight">{booking.name}</p>
              <p className="text-black/50 text-xs mt-1">
                {booking.sport} · {dateLabel}
              </p>
              <p className="text-black/40 text-xs mt-0.5">
                {sorted.length
                  ? `${formatTime12(sorted[0])} → ${formatTime12(sorted[sorted.length - 1])}`
                  : "—"}
              </p>
              <p className="text-black/30 text-xs mt-0.5">{booking.id} · {booking.phone}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[10px] text-black/35 font-semibold mb-0.5">Booking Amount</p>
              <p className="text-black font-black text-xl" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
                ₹{(booking.total ?? 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Refund amount display */}
        {hasPaymentId && (
          <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-green-50 border border-green-100 mb-4">
            <span className="text-green-700 text-sm font-semibold">Refund Amount</span>
            <span className="text-green-700 font-black text-base">₹{(booking.total ?? 0).toLocaleString()}</span>
          </div>
        )}

        {/* Refund action */}
        <div className="mb-4">
          <label className="block text-black font-bold text-sm mb-1.5">Refund Action</label>
          <select
            className="w-full border border-black/10 rounded-xl px-4 py-2.5 text-sm text-black bg-black/[0.03] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/40 transition-all"
            value={refundStatus}
            onChange={(e) => setRefundStatus(e.target.value)}
          >
            {REFUND_OPTIONS
              .filter((o) => hasPaymentId || o.value !== "process_now")
              .map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
          </select>
        </div>

        {/* Warning when processing refund via API */}
        {refundStatus === "process_now" && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-amber-800 text-xs font-semibold leading-relaxed">
              ⚠️ <strong>This will immediately refund ₹{(booking.total ?? 0).toLocaleString()} to the customer via Razorpay.</strong> The money leaves your account the moment you confirm. This cannot be undone.
            </p>
          </div>
        )}

        {/* API error */}
        {apiError && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200">
            <p className="text-red-700 text-xs font-semibold">Refund API failed: {apiError}</p>
            <p className="text-red-500 text-xs mt-1">The booking was NOT cancelled. Fix the issue and try again, or process the refund manually in Razorpay then cancel with "Refund Pending".</p>
          </div>
        )}

        {/* Notes */}
        <div className="mb-6">
          <label className="block text-black font-bold text-sm mb-1.5">Cancellation Notes <span className="text-black/30 font-normal">(optional)</span></label>
          <textarea
            rows={2}
            placeholder="Reason for cancellation..."
            className="w-full border border-black/10 rounded-xl px-4 py-2.5 text-sm text-black bg-black/[0.03] placeholder-black/25 focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/40 resize-none transition-all"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-black/10 text-black/50 text-sm font-semibold hover:bg-black/[0.03] transition-all"
          >
            Keep Booking
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirming}
            className="flex-1 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-black text-sm transition-all disabled:opacity-60"
          >
            {confirming ? "Cancelling…" : "Confirm Cancel"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Revenue Chart ─────────────────────────────────────────────────────────────

const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Build a local YYYY-MM-DD string without UTC conversion (avoids midnight-IST → prev-UTC-day bug)
function localDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getRevenueData(range, bookings) {
  const today = new Date();
  const confirmed = bookings.filter((b) => ["confirmed", "completed", "upcoming"].includes(b.status));

  if (range === "All Time") {
    // Group by month — one bar per month
    const byMonth = {};
    confirmed.forEach((b) => {
      if (!b.date) return;
      const month = b.date.slice(0, 7); // "YYYY-MM"
      byMonth[month] = (byMonth[month] || 0) + (b.total || 0);
    });
    const months = Object.keys(byMonth).sort();
    if (months.length === 0) {
      // No data yet — show last 6 months empty so chart renders
      return Array.from({ length: 6 }, (_, i) => {
        const d = new Date(today.getFullYear(), today.getMonth() - 5 + i, 1);
        return { label: MONTH_SHORT[d.getMonth()], revenue: 0, date: localDateStr(d) };
      });
    }
    return months.map((m) => {
      const [y, mo] = m.split("-").map(Number);
      return { label: MONTH_SHORT[mo - 1], revenue: byMonth[m] || 0, date: m };
    });
  }

  let dates = [];
  if (range === "7 Days" || range === "14 Days" || range === "30 Days") {
    const n = range === "7 Days" ? 7 : range === "14 Days" ? 14 : 30;
    dates = Array.from({ length: n }, (_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() - (n - 1 - i));
      return d;
    });
  } else {
    // This Month — days 1 through today
    const y = today.getFullYear(), m = today.getMonth();
    dates = Array.from({ length: today.getDate() }, (_, i) => new Date(y, m, i + 1));
  }

  const revenueByDate = {};
  confirmed.forEach((b) => {
    if (!b.date) return;
    revenueByDate[b.date] = (revenueByDate[b.date] || 0) + (b.total || 0);
  });

  return dates.map((d) => {
    const dateStr = localDateStr(d);
    const label = range === "7 Days" ? DAY_SHORT[d.getDay()] : String(d.getDate());
    return { label, revenue: revenueByDate[dateStr] || 0, date: dateStr };
  });
}

const RANGE_OPTIONS = ["7 Days", "14 Days", "30 Days", "This Month", "All Time"];

// Returns true if booking date falls within the selected range
function inRange(dateStr, range) {
  if (range === "All Time") return true;
  const today = new Date();
  const bDate = new Date(dateStr + "T00:00:00");
  if (range === "This Month") {
    return bDate.getMonth() === today.getMonth() && bDate.getFullYear() === today.getFullYear();
  }
  const n = range === "7 Days" ? 7 : range === "14 Days" ? 14 : 30;
  const cutoff = new Date(today);
  cutoff.setDate(today.getDate() - (n - 1));
  cutoff.setHours(0, 0, 0, 0);
  return bDate >= cutoff;
}

function RevenueChart({ bookings }) {
  const [range, setRange] = useState("7 Days");
  const data = getRevenueData(range, bookings);
  const total = data.reduce((s, d) => s + d.revenue, 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{
        background: "white", border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 10, padding: "8px 14px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        fontFamily: "Inter, system-ui, sans-serif",
      }}>
        <p style={{ fontSize: 11, color: "rgba(0,0,0,0.4)", fontWeight: 600, marginBottom: 2 }}>{label}</p>
        <p style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>
          ₹{payload[0].value.toLocaleString()}
        </p>
      </div>
    );
  };

  return (
    <div className="bg-white border border-black/8 rounded-2xl p-5 mb-6">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <p className="text-xs font-black text-black/40 uppercase tracking-[0.18em] mb-0.5">Total Revenue</p>
          <p className="text-black font-bold text-2xl leading-none" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
            ₹{total.toLocaleString()}
          </p>
        </div>
        {/* Range filters */}
        <div className="flex items-center gap-1 p-1 rounded-xl border border-black/8" style={{ background: "#f9fafb" }}>
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                range === r ? "bg-[#f59e0b] text-black shadow-sm" : "text-black/40 hover:text-black"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Bar chart */}
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} barCategoryGap={data.length > 14 ? "15%" : "30%"} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: data.length > 14 ? 9 : 11, fill: "rgba(0,0,0,0.35)", fontWeight: 600, fontFamily: "Inter, system-ui, sans-serif" }}
            interval={data.length > 14 ? Math.floor(data.length / 10) : 0}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)", radius: 6 }} />
          <Bar dataKey="revenue" radius={[5, 5, 0, 0]}>
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.revenue > 0 ? "#f59e0b" : "rgba(0,0,0,0.07)"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Donut gauge (SVG) ─────────────────────────────────────────────────────────

function DonutGauge({ percent, color }) {
  const r = 30;
  const circ = 2 * Math.PI * r;
  const filled = circ * Math.min(percent, 100) / 100;
  return (
    <svg width={76} height={76} viewBox="0 0 76 76">
      {/* Track */}
      <circle cx={38} cy={38} r={r} fill="none" stroke="rgba(0,0,0,0.07)" strokeWidth={7} />
      {/* Progress */}
      <circle
        cx={38} cy={38} r={r}
        fill="none"
        stroke={color}
        strokeWidth={7}
        strokeDasharray={`${filled} ${circ - filled}`}
        strokeLinecap="round"
        transform="rotate(-90 38 38)"
        style={{ transition: "stroke-dasharray 0.5s ease" }}
      />
      {/* Label */}
      <text
        x={38} y={43}
        textAnchor="middle"
        fontSize={13}
        fontWeight={700}
        fill="#111"
        fontFamily="Inter, system-ui, sans-serif"
      >
        {percent}%
      </text>
    </svg>
  );
}

// ── Individual Court Card ─────────────────────────────────────────────────────

function CourtCard({ courtName, sportBookings, courtIdx, numCourts, cfg, today }) {
  const myBookings = numCourts === 1
    ? sportBookings
    : sportBookings.filter((b) => b.courtNumber === courtIdx + 1);
  const todayBookings = myBookings.filter((b) => b.date === today);
  const todayCount = todayBookings.length;
  const todaySlots = todayBookings.reduce((s, b) => s + (b.slots?.length || 0), 0);
  const occupancy = Math.round((todaySlots / TOTAL_SLOTS_PER_DAY) * 100);
  const revenue = todayBookings.reduce((s, b) => s + (b.total || 0), 0);

  return (
    <div className="bg-white border border-black/8 rounded-2xl p-6 shadow-sm flex flex-col gap-5">
      {/* Top row: name + badge + donut */}
      <div className="flex items-start justify-between gap-4">
        <div className="pt-1">
          <p className="text-black font-bold text-base leading-tight">{courtName}</p>
          <span
            className="inline-block mt-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full"
            style={{ background: cfg.pillBg, color: cfg.pillText, border: `1px solid ${cfg.pillBorder}` }}
          >
            {cfg.sport}
          </span>
        </div>
        <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
          <DonutGauge percent={occupancy} color={cfg.accentLight} />
          <p className="text-[10px] text-black/35 font-semibold">Occupancy Today</p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 pt-5 border-t border-black/6">
        <div>
          <p className="text-xs text-black/35 font-semibold mb-1.5">Today's Bookings</p>
          <p
            className="text-black font-bold text-3xl leading-none"
            style={{ fontFamily: "Inter, system-ui, sans-serif" }}
          >
            {todayCount}
          </p>
        </div>
        <div>
          <p className="text-xs text-black/35 font-semibold mb-1.5">Today's Revenue</p>
          <p
            className="font-bold text-3xl leading-none"
            style={{ fontFamily: "Inter, system-ui, sans-serif", color: cfg.accentLight }}
          >
            ₹{revenue.toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Courts Tab ────────────────────────────────────────────────────────────────

function CourtsTab({ bookings }) {
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="space-y-8">
      {/* Section heading */}
      <div className="flex items-center gap-3">
        <p className="text-xs font-black text-black/40 uppercase tracking-[0.18em]">Today's Court Schedule</p>
        <div className="flex-1 h-px bg-black/6" />
        <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-black/[0.04] text-black/40 border border-black/8">
          Today
        </span>
      </div>

      {COURTS_CONFIG.map((cfg) => {
        const sportBookings = bookings.filter((b) => b.sport === cfg.sport && b.status === "confirmed");
        const cols =
          cfg.courts.length === 1 ? "grid-cols-1" :
          cfg.courts.length === 2 ? "grid-cols-1 sm:grid-cols-2" :
          "grid-cols-1 sm:grid-cols-3";

        return (
          <div key={cfg.sport}>
            {/* Sport header */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-lg">{cfg.emoji}</span>
              <span className="text-black font-black text-sm">{cfg.sport}</span>
              <span
                className="text-[10px] font-bold px-2.5 py-1 rounded-full"
                style={{ background: cfg.pillBg, color: cfg.pillText, border: `1px solid ${cfg.pillBorder}` }}
              >
                {cfg.tag}
              </span>
              <div className="flex-1 h-px bg-black/6" />
            </div>

            <div className={`grid ${cols} gap-4`}>
              {cfg.courts.map((courtName, ci) => (
                <CourtCard
                  key={courtName}
                  courtName={courtName}
                  sportBookings={sportBookings}
                  courtIdx={ci}
                  numCourts={cfg.courts.length}
                  cfg={cfg}
                  today={today}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Block Dates Tab ───────────────────────────────────────────────────────────

const BLOCK_SLOTS = [
  "05:00","06:00","07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00",
  "15:00","16:00","17:00","18:00","19:00","20:00","21:00","22:00","23:00",
  "00:00","01:00","02:00","03:00","04:00",
];

function fmtSlotRange(t) {
  const h = parseInt(t, 10);
  const endH = (h + 1) % 24;
  const fmt = (n) => `${n % 12 || 12}:00 ${n >= 12 ? "PM" : "AM"}`;
  return `${fmt(h)} – ${fmt(endH)}`;
}

const BLOCK_COURT_OPTIONS = [
  "All Courts",
  "Oval Cricket",
  "Box Cricket Court 1",
  "Box Cricket Court 2",
  "Pickleball Court 1",
  "Pickleball Court 2",
];

function BlockDatesTab({ setToast }) {
  const { blocks, createBlock, deleteBlock, bookings } = useBooking();
  const todayStr = new Date().toISOString().split("T")[0];

  const [blockDate, setBlockDate]     = useState("");
  const [blockSlots, setBlockSlots]   = useState([]);
  const [blockReason, setBlockReason] = useState("Maintenance");
  const [blockCourts, setBlockCourts] = useState(["all"]);
  const [saving, setSaving]           = useState(false);
  const [conflictWarning, setConflictWarning] = useState(null); // { conflicts: [...], proceed: fn }

  const allSelected = blockSlots.length === BLOCK_SLOTS.length;

  // Explicit save-readiness check — easier to debug than inline boolean
  const canSave = blockDate.length > 0 && blockSlots.length > 0 && blockCourts.length > 0 && !saving;

  const toggleBlockSlot = (slot) => {
    setBlockSlots((prev) => {
      const next = prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot];
      console.log("[BlockDatesTab] slots →", next.length, "selected", next);
      return next;
    });
  };

  const handleAllDay = (checked) => {
    const next = checked ? [...BLOCK_SLOTS] : [];
    console.log("[BlockDatesTab] allDay →", checked, "slots set to", next.length);
    setBlockSlots(next);
  };

  const handleCourtToggle = (option) => {
    setBlockCourts((prev) => {
      let next;
      if (option === "All Courts") {
        next = ["all"];
      } else {
        const withoutAll = prev.filter((c) => c !== "all");
        const toggled = withoutAll.includes(option)
          ? withoutAll.filter((c) => c !== option)
          : [...withoutAll, option];
        next = toggled.length === 0 ? ["all"] : toggled;
      }
      console.log("[BlockDatesTab] courts →", next);
      return next;
    });
  };

  const doSaveBlock = async () => {
    setSaving(true);
    try {
      const regular = blockSlots.filter((s) => !NEXT_DAY_SLOTS.has(s));
      const nextDay = blockSlots.filter((s) =>  NEXT_DAY_SLOTS.has(s));
      await createBlock(blockDate, regular, nextDay, blockReason, blockCourts);
      const courtLabel = blockCourts.includes("all") ? "all courts" : blockCourts.join(", ");
      logAdminAction("block_slots", `Blocked ${blockSlots.length} slot(s) on ${blockDate} — ${courtLabel} — reason: ${blockReason}`);
      setToast(`${blockSlots.length} slot${blockSlots.length > 1 ? "s" : ""} blocked on ${blockDate}`);
      setBlockDate("");
      setBlockSlots([]);
      setBlockReason("Maintenance");
      setBlockCourts(["all"]);
    } catch (e) {
      console.error("[BlockDatesTab] createBlock failed:", e);
      setToast("Failed to save block");
    }
    setSaving(false);
  };

  const handleBlock = async () => {
    console.log("[BlockDatesTab] handleBlock — date:", blockDate, "slots:", blockSlots.length, "courts:", blockCourts, "canSave:", canSave);
    if (!canSave) return;

    // Check for confirmed bookings that overlap with the slots being blocked
    const regular = blockSlots.filter((s) => !NEXT_DAY_SLOTS.has(s));
    const nextDay  = blockSlots.filter((s) =>  NEXT_DAY_SLOTS.has(s));
    const blockingAll = blockCourts.includes("all");

    const sportMatchesCourts = (sport) => {
      if (blockingAll) return true;
      if (sport === "Oval Cricket") return blockCourts.some((c) => c === "Oval Cricket");
      if (sport === "Box Cricket")  return blockCourts.some((c) => c.startsWith("Box Cricket"));
      if (sport === "Pickleball")   return blockCourts.some((c) => c.startsWith("Pickleball"));
      return false;
    };

    const conflicts = bookings.filter((b) => {
      if (b.status !== "confirmed") return false;
      if (b.date !== blockDate) return false;
      if (!sportMatchesCourts(b.sport)) return false;
      return regular.some((s) => (b.slots || []).includes(s)) ||
             nextDay.some((s)  => (b.nextDaySlots || []).includes(s));
    });

    if (conflicts.length > 0) {
      setConflictWarning({ conflicts, proceed: doSaveBlock });
      return;
    }
    await doSaveBlock();
  };

  const handleUnblock = async (bl) => {
    console.log("[BlockDatesTab] unblocking", bl.firestoreId, bl.date);
    try {
      await deleteBlock(bl.firestoreId);
      logAdminAction("unblock_slots", `Unblocked ${bl.date} (${bl.firestoreId})`);
      setToast(`Block removed for ${bl.date}`);
    } catch (e) {
      console.error("[BlockDatesTab] deleteBlock failed:", e);
      setToast("Failed to remove block");
    }
  };

  const sortedBlocks = [...blocks].sort((a, b) => (a.date < b.date ? -1 : 1));

  const fmt12Short = (t) => {
    const h = parseInt(t, 10);
    return `${h % 12 || 12}${h >= 12 ? "p" : "a"}`;
  };

  return (
    <div className="space-y-5">

      {/* ── Block form ── */}
      <div className="bg-white border border-black/8 rounded-2xl p-5">
        <p className="text-xs font-black text-black/40 uppercase tracking-[0.18em] mb-4">Block Time Slots</p>

        {/* Court selector */}
        <div className="mb-4">
          <label className="block text-black font-bold text-xs mb-1.5">Courts</label>
          <div className="flex flex-wrap gap-1.5">
            {BLOCK_COURT_OPTIONS.map((option) => {
              const active = option === "All Courts"
                ? blockCourts.includes("all")
                : !blockCourts.includes("all") && blockCourts.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleCourtToggle(option)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                    active
                      ? "bg-black text-white border-black"
                      : "bg-white text-black/50 border-black/10 hover:border-black/30 hover:text-black"
                  }`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>

        {/* Date + Reason */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-black font-bold text-xs mb-1.5">Date</label>
            <input
              type="date"
              min={todayStr}
              className="w-full border border-black/10 rounded-xl px-3 py-2.5 text-sm text-black bg-black/[0.02] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 transition-all"
              value={blockDate}
              onChange={(e) => {
                console.log("[BlockDatesTab] date →", e.target.value);
                setBlockDate(e.target.value);
              }}
            />
          </div>
          <div>
            <label className="block text-black font-bold text-xs mb-1.5">Reason</label>
            <select
              className="w-full border border-black/10 rounded-xl px-3 py-2.5 text-sm text-black bg-black/[0.02] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 transition-all"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
            >
              {REASONS.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
        </div>

        {/* All-day checkbox */}
        <label className="flex items-center gap-2.5 mb-3 cursor-pointer w-fit">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(e) => handleAllDay(e.target.checked)}
            className="w-4 h-4 accent-[#f59e0b] cursor-pointer"
          />
          <span className="text-black font-bold text-sm">Block entire day (all 22 slots)</span>
        </label>

        {/* Slot picker */}
        <div className="border border-black/8 rounded-xl p-3 bg-black/[0.01]">
          <p className="text-[10px] font-black text-black/30 uppercase tracking-[0.18em] mb-2.5">Select slots to block</p>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-11 gap-1.5">
            {BLOCK_SLOTS.map((slot) => {
              const h = parseInt(slot, 10);
              const endH = (h + 1) % 24;
              const label = `${h % 12 || 12}${h >= 12 ? "p" : "a"}–${endH % 12 || 12}${endH >= 12 ? "p" : "a"}`;
              const selected = blockSlots.includes(slot);
              return (
                <button
                  key={slot}
                  type="button"
                  onClick={() => toggleBlockSlot(slot)}
                  className={`py-2 px-1 rounded-lg text-[10px] font-bold transition-all border flex flex-col items-center gap-0.5 ${
                    selected
                      ? "bg-red-500 border-red-500 text-white shadow-sm"
                      : "bg-white border-black/8 text-black/50 hover:border-red-300 hover:text-red-500"
                  }`}
                >
                  {label}
                  {NEXT_DAY_SLOTS.has(slot) && (
                    <span className={`text-[7px] font-bold leading-none ${selected ? "text-white/70" : "text-black/30"}`}>+1d</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Status + Save button */}
        <div className="flex items-center justify-between mt-4">
          <p className="text-black/40 text-xs">
            {blockSlots.length > 0
              ? `${blockSlots.length} slot${blockSlots.length > 1 ? "s" : ""} · ${blockCourts.includes("all") ? "all courts" : blockCourts.join(", ")}`
              : !blockDate ? "Pick a date to start" : "Select slots or check 'Block entire day'"}
          </p>
          <button
            type="button"
            onClick={handleBlock}
            disabled={!canSave}
            className={`text-white font-black text-sm px-5 py-2.5 rounded-xl transition-all ${
              canSave ? "bg-black hover:bg-black/80" : "bg-black/30 cursor-not-allowed"
            }`}
          >
            {saving ? "Saving..." : "Block Slots"}
          </button>
        </div>
      </div>

      {/* ── Conflict warning modal ── */}
      {conflictWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <p className="text-sm font-black text-red-600 mb-1">Warning: Confirmed Bookings Exist</p>
            <p className="text-xs text-black/50 mb-3">The following confirmed bookings overlap with the slots you're about to block:</p>
            <div className="space-y-2 mb-4">
              {conflictWarning.conflicts.map((b) => {
                const allS = [...(b.slots || []), ...(b.nextDaySlots || [])].sort();
                const slotRange = allS.length > 0
                  ? `${fmt12Short(allS[0])}–${fmt12Short(String(parseInt(allS[allS.length - 1], 10) + 1).padStart(2, "0"))}`
                  : "";
                return (
                  <div key={b.id} className="bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                    <p className="text-xs font-bold text-black">{b.name} · {b.phone}</p>
                    <p className="text-[10px] text-black/40">{b.id} · {b.sport}{b.courtNumber ? ` Court ${b.courtNumber}` : ""} · {slotRange}</p>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConflictWarning(null)}
                className="flex-1 py-2.5 rounded-xl border border-black/10 text-sm font-bold text-black/60 hover:bg-black/5 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  setConflictWarning(null);
                  await conflictWarning.proceed();
                }}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-all"
              >
                Block Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── All blocks list ── */}
      <div className="bg-white border border-black/8 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-black text-black/40 uppercase tracking-[0.18em]">
            All Blocks ({sortedBlocks.length})
          </p>
          {sortedBlocks.some((bl) => bl.date < todayStr) && (
            <button
              type="button"
              onClick={async () => {
                const stale = sortedBlocks.filter((bl) => bl.date < todayStr);
                await Promise.all(stale.map((bl) => deleteBlock(bl.firestoreId)));
                setToast(`Removed ${stale.length} stale block${stale.length !== 1 ? "s" : ""}`);
              }}
              className="text-[10px] font-bold px-3 py-1.5 rounded-xl border border-black/10 text-black/40 hover:border-red-300 hover:text-red-500 hover:bg-red-50 transition-all"
            >
              Auto-cleanup past
            </button>
          )}
        </div>
        {sortedBlocks.length === 0 ? (
          <p className="text-black/30 text-sm py-4 text-center">No blocks saved yet</p>
        ) : (
          <div className="space-y-2">
            {sortedBlocks.map((bl) => {
              const allSlots = [...(bl.slots || []), ...(bl.nextDaySlots || [])];
              const isPast = bl.date < todayStr;
              const dateLabel = new Date(bl.date + "T00:00:00").toLocaleDateString("en-IN", {
                weekday: "short", day: "numeric", month: "short", year: "numeric",
              });
              const courtsLabel = (!bl.courts || bl.courts.includes("all")) ? "All Courts" : bl.courts.join(", ");
              const createdLabel = bl.createdAt?.seconds
                ? new Date(bl.createdAt.seconds * 1000).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
                : "";
              return (
                <div
                  key={bl.firestoreId}
                  className={`flex items-start justify-between gap-3 py-3 px-4 rounded-xl border border-black/6 transition-opacity ${isPast ? "opacity-40" : ""}`}
                  style={{ background: "#f9fafb" }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-black font-semibold text-sm">{dateLabel}</p>
                      {isPast && <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-black/5 text-black/30 uppercase tracking-wide">Past</span>}
                    </div>
                    <p className="text-black/40 text-xs mt-0.5">
                      🏟️ {courtsLabel} · 🔒 {bl.reason} · {allSlots.length} slot{allSlots.length !== 1 ? "s" : ""}
                      {createdLabel ? ` · added ${createdLabel}` : ""}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {allSlots.sort().map((s) => (
                        <span key={s} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-50 border border-red-100 text-red-500">
                          {fmt12Short(s)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleUnblock(bl)}
                    className="flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 active:bg-red-100 transition-all"
                  >
                    Unblock
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}

// ── Court Price Card ──────────────────────────────────────────────────────────

function CourtPriceCard({ court, prices, onChange, onSave }) {
  const emoji = court.startsWith("Oval") ? "🏟️" : court.startsWith("Box") ? "🏏" : "🏓";
  const fields = [
    { key: "weekdayDay",   label: "Weekday Day ☀️ (₹)",   note: "06:00–17:00" },
    { key: "weekdayNight", label: "Weekday Night 🌙 (₹)",  note: "18:00–05:00" },
    { key: "weekendDay",   label: "Weekend Day ☀️ (₹)",    note: "06:00–17:00" },
    { key: "weekendNight", label: "Weekend Night 🌙 (₹)",  note: "18:00–05:00" },
  ];
  return (
    <div className="bg-white border border-black/8 rounded-2xl p-5 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <span className="text-lg">{emoji}</span>
        <p className="text-black font-bold text-sm leading-tight">{court}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {fields.map(({ key, label, note }) => (
          <div key={key}>
            <label className="block text-black/40 text-xs font-semibold mb-0.5">{label}</label>
            <p className="text-black/25 text-[10px] mb-1.5">{note}</p>
            <input
              type="number"
              className="w-full border border-black/10 rounded-xl px-3 py-2.5 text-sm text-black bg-black/[0.03] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 transition-all"
              value={prices[key] ?? ""}
              onChange={(e) => onChange(key, e.target.value)}
            />
          </div>
        ))}
      </div>
      <button
        onClick={onSave}
        className="w-full bg-[#f59e0b] hover:bg-amber-400 text-black font-black text-sm py-2.5 rounded-xl transition-all"
      >
        Save
      </button>
    </div>
  );
}

// ── Webhook Logs Tab ──────────────────────────────────────────────────────────

// Returns badge + dot style based on event type and action
function getLogStyle(log) {
  if (!log.ok || log.action === "error") {
    return { bg: "bg-black/5", border: "border-black/10", text: "text-black/40", dot: "bg-black/30", isError: true };
  }
  if (log.eventType === "payment.captured" && log.action === "confirmed") {
    return { bg: "bg-green-50", border: "border-green-100", text: "text-green-700", dot: "bg-green-500", isError: false };
  }
  if (log.eventType === "payment.captured") {
    return { bg: "bg-green-50/50", border: "border-green-100", text: "text-green-600/60", dot: "bg-green-300", isError: false };
  }
  if (log.eventType === "payment.failed") {
    return { bg: "bg-red-50", border: "border-red-100", text: "text-red-600", dot: "bg-red-500", isError: false };
  }
  if (log.eventType === "refund.created") {
    return { bg: "bg-orange-50", border: "border-orange-100", text: "text-orange-700", dot: "bg-orange-400", isError: false };
  }
  if (log.eventType === "signature_failed") {
    return { bg: "bg-amber-50", border: "border-amber-100", text: "text-amber-700", dot: "bg-amber-400", isError: false };
  }
  return { bg: "bg-black/5", border: "border-black/10", text: "text-black/40", dot: "bg-black/30", isError: false };
}

function WebhookLogsTab() {
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearing, setClearing]     = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, "webhook_logs"),
      orderBy("timestamp", "desc"),
      limit(50)
    );
    const unsub = onSnapshot(q, (snap) => {
      setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error("[WebhookLogs] onSnapshot error:", err.message);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const errorLogs  = logs.filter((l) => !l.ok || l.action === "error");
  const captured   = logs.filter((l) => l.eventType === "payment.captured" && l.action === "confirmed").length;
  const failed     = logs.filter((l) => l.eventType === "payment.failed").length;
  const refunded   = logs.filter((l) => l.eventType === "refund.created").length;
  const sigFailed  = logs.filter((l) => l.eventType === "signature_failed").length;

  const handleClearErrors = async () => {
    setClearing(true);
    try {
      await Promise.all(errorLogs.map((l) => deleteDoc(doc(db, "webhook_logs", l.id))));
    } catch (e) {
      console.error("[WebhookLogs] Clear failed:", e.message);
    }
    setClearing(false);
    setShowClearModal(false);
  };

  return (
    <div className="space-y-5">
      {/* Summary pills */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Payments Confirmed", value: captured,  color: "text-green-700",  bg: "bg-green-50 border-green-100"   },
          { label: "Payments Failed",    value: failed,    color: "text-red-600",    bg: "bg-red-50 border-red-100"       },
          { label: "Refunds",            value: refunded,  color: "text-orange-700", bg: "bg-orange-50 border-orange-100" },
          { label: "Bad Signatures",     value: sigFailed, color: "text-amber-700",  bg: "bg-amber-50 border-amber-100"   },
        ].map((s) => (
          <div key={s.label} className={`rounded-2xl border p-4 ${s.bg}`}>
            <p className="text-xs font-semibold text-black/40 mb-1.5">{s.label}</p>
            <p className={`text-2xl font-black leading-none ${s.color}`} style={{ fontFamily: "Inter, system-ui, sans-serif" }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Logs list */}
      <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-black/6 flex items-center justify-between" style={{ background: "#f9fafb" }}>
          <p className="text-xs font-black text-black/40 uppercase tracking-[0.18em]">Recent Events ({logs.length})</p>
          <div className="flex items-center gap-3">
            {errorLogs.length > 0 && (
              <button
                onClick={() => setShowClearModal(true)}
                className="text-[10px] font-bold text-red-500 hover:text-red-600 border border-red-200 hover:border-red-300 bg-red-50 hover:bg-red-100 px-2.5 py-1 rounded-lg transition-all"
              >
                Clear {errorLogs.length} Error{errorLogs.length !== 1 ? "s" : ""}
              </button>
            )}
            <span className="text-[10px] text-black/30 font-medium">Last 50 · live</span>
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-black/30 text-sm">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-2xl mb-2">🔔</p>
            <p className="text-black/30 text-sm">No webhook events yet</p>
            <p className="text-black/20 text-xs mt-1">Events appear here as Razorpay sends them</p>
          </div>
        ) : (
          <div className="divide-y divide-black/5">
            {logs.map((log) => {
              const s = getLogStyle(log);
              const ts = log.timestamp?.seconds
                ? new Date(log.timestamp.seconds * 1000).toLocaleString("en-IN", {
                    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit",
                  })
                : "—";
              return (
                <div key={log.id} className="px-5 py-3.5 flex items-start gap-3">
                  <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border uppercase tracking-wide ${s.bg} ${s.border} ${s.text}`}>
                        {s.isError && <span className="mr-1">⚠</span>}
                        {log.eventType}
                      </span>
                      {log.action && (
                        <span className="text-[10px] text-black/30 font-semibold">{log.action}</span>
                      )}
                      {!log.ok && log.error && (
                        <span className="text-[10px] text-black/40 italic">{log.error}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {log.bookingId && (
                        <span className="text-xs text-black font-bold">{log.bookingId}</span>
                      )}
                      {log.paymentId && (
                        <span className="text-[10px] text-black/40 font-mono">{log.paymentId}</span>
                      )}
                      {log.amount != null && (
                        <span className="text-[10px] text-black/50 font-semibold">₹{log.amount}</span>
                      )}
                      <span className="text-[10px] text-black/25">{ts}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Clear errors confirmation modal */}
      <AnimatePresence>
        {showClearModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.45)" }}
            onClick={() => !clearing && setShowClearModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="font-black text-black text-base mb-1">Clear Error Logs?</p>
              <p className="text-black/50 text-sm mb-5">
                This will permanently delete {errorLogs.length} error log{errorLogs.length !== 1 ? "s" : ""} from Firestore. Successful events are not affected.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearModal(false)}
                  disabled={clearing}
                  className="flex-1 border border-black/10 text-black/60 font-bold text-sm py-2.5 rounded-xl hover:bg-black/5 transition-all disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearErrors}
                  disabled={clearing}
                  className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-black text-sm py-2.5 rounded-xl transition-all"
                >
                  {clearing ? "Deleting…" : "Delete Errors"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Error Logs Tab ─────────────────────────────────────────────────────────────

function ErrorLogsTab() {
  const [logs, setLogs]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "booking_errors"), orderBy("timestamp", "desc"), limit(100));
    const unsub = onSnapshot(q, (snap) => {
      setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error("[ErrorLogs] onSnapshot error:", err.message);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  function fmtTs(ts) {
    if (!ts?.seconds) return "—";
    return new Date(ts.seconds * 1000).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", hour12: true,
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  }

  return (
    <div className="space-y-5">
      <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-black/6 flex items-center justify-between" style={{ background: "#f9fafb" }}>
          <p className="text-xs font-black text-black/40 uppercase tracking-[0.18em]">
            Booking Creation Errors ({logs.length})
          </p>
          <span className="text-[10px] text-black/30 font-medium">Last 100 · live</span>
        </div>

        {loading ? (
          <div className="py-12 text-center text-black/30 text-sm">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-2xl mb-2">✅</p>
            <p className="text-black/30 text-sm">No booking errors recorded</p>
            <p className="text-black/20 text-xs mt-1">Errors appear here when createPendingBooking fails unexpectedly</p>
          </div>
        ) : (
          <div className="divide-y divide-black/5">
            {logs.map((log) => (
              <div key={log.id} className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <span className="mt-1 w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    {/* Top row: timestamp + customer */}
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      <span className="text-[10px] text-black/30">{fmtTs(log.timestamp)}</span>
                      {log.phone && <span className="text-xs font-bold text-black">{log.phone}</span>}
                      {log.name  && <span className="text-xs text-black/50">{log.name}</span>}
                    </div>
                    {/* Error message */}
                    <p className="text-sm font-semibold text-red-700 mb-1">
                      {log.errorMessage || "(no message)"}
                      {log.errorCode && <span className="ml-2 text-[10px] font-mono text-red-400">{log.errorCode}</span>}
                    </p>
                    {/* Booking details */}
                    <div className="flex items-center gap-3 flex-wrap text-[11px] text-black/40">
                      {log.sport && <span>{log.sport}</span>}
                      {log.date  && <span>{log.date}</span>}
                      {log.slots?.length > 0 && <span>{log.slots.join(", ")}</span>}
                      {log.totalAttempted != null && <span>₹{log.totalAttempted.toLocaleString()}</span>}
                    </div>
                    {/* Device */}
                    {log.userAgent && (
                      <p className="text-[10px] text-black/25 mt-1 truncate">{log.userAgent}</p>
                    )}
                    {/* Stack trace (collapsed) */}
                    {log.errorStack && (
                      <details className="mt-2">
                        <summary className="text-[10px] text-black/30 cursor-pointer hover:text-black/50">Stack trace</summary>
                        <pre className="text-[9px] text-black/40 font-mono mt-1 whitespace-pre-wrap break-all bg-black/[0.03] rounded-lg p-2 max-h-32 overflow-y-auto">
                          {log.errorStack}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function AdminDashboard({ role = "viewer" }) {
  const isSuper = role === "super";
  const navigate = useNavigate();
  const { bookings, cancelBooking } = useBooking();

  const handleLogout = () => {
    sessionStorage.removeItem("tsarena_admin_auth");
    sessionStorage.removeItem("adminRole");
    navigate("/");
  };

  const [tab, setTab] = useState("bookings");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("Today");
  const [statusFilter, setStatusFilter] = useState(null); // null | "upcoming" | "completed"
  const [upcomingSubFilter, setUpcomingSubFilter] = useState("All Upcoming");
  const [failureReasonFilter, setFailureReasonFilter] = useState(""); // "" | reason key
  const [showRevTooltip, setShowRevTooltip] = useState(false);

  // Run orphaned-lock cleanup once on admin mount — self-heals any locks whose
  // parent booking failed/cancelled without the lock being properly released.
  useEffect(() => {
    cleanupOrphanedLocks().catch((e) =>
      console.warn("[AdminDashboard] Orphan cleanup error (non-fatal):", e.message)
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [toast, setToast] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const bookingsSectionRef = useRef(null);


  const { promo } = usePromo();
  const [promoForm, setPromoForm] = useState(PROMO_DEFAULTS);
  const [promoSaving, setPromoSaving] = useState(false);

  // Seed local form from PromoContext whenever Firebase updates
  useEffect(() => { setPromoForm(promo); }, [promo]);

  // courtPrices is local editable state; seeded from PricingContext (which syncs from Firestore)
  const { pricing } = usePricing();
  const [courtPrices, setCourtPrices] = useState(PRICING_DEFAULTS);

  // Keep courtPrices in sync with PricingContext (fires whenever Firestore updates)
  useEffect(() => {
    setCourtPrices((prev) => ({ ...prev, ...pricing }));
  }, [pricing]);

  const todayStr = new Date().toISOString().split("T")[0];

  const totalRevenue = bookings
    .filter((b) => b.status === "confirmed" || b.status === "completed" || b.status === "upcoming")
    .reduce((s, b) => s + (b.total || 0), 0);

  const onlineRevenue = bookings
    .filter((b) => ["confirmed","completed","upcoming"].includes(b.status) && b.source !== "manual")
    .reduce((s, b) => s + (b.total || 0), 0);
  const manualRevenue = bookings
    .filter((b) => ["confirmed","completed","upcoming"].includes(b.status) && b.source === "manual")
    .reduce((s, b) => s + (b.total || 0), 0);

  const upcomingConfirmedCount = bookings.filter((b) => b.status === "confirmed" && b.date >= todayStr).length;
  const completedCount         = bookings.filter((b) => b.status === "completed").length;
  // Counts for upcoming sub-filter pills
  const nextWeekStr = (() => {
    const d = new Date(todayStr + "T00:00:00"); d.setDate(d.getDate() + 7); return localDateStr(d);
  })();
  const monthEndStr = (() => {
    const now = new Date(); return localDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  })();
  const upcomingCounts = {
    "Today":       bookings.filter((b) => b.status === "confirmed" && b.date === todayStr).length,
    "This Week":   bookings.filter((b) => b.status === "confirmed" && b.date >= todayStr && b.date <= nextWeekStr).length,
    "This Month":  bookings.filter((b) => b.status === "confirmed" && b.date >= todayStr && b.date <= monthEndStr).length,
    "All Upcoming": upcomingConfirmedCount,
  };

  const handleStatCard = (action) => {
    if (action === "revenue") { setShowRevTooltip((v) => !v); return; }
    setShowRevTooltip(false);
    setTab("bookings");
    if (action === "total")     { setStatusFilter(null); setDateFilter("All Time"); }
    if (action === "upcoming")  { setStatusFilter("upcoming"); setUpcomingSubFilter("All Upcoming"); }
    if (action === "completed") { setStatusFilter("completed"); }
    setTimeout(() => bookingsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const stats = [
    { label: "Total Bookings", value: bookings.length,                          icon: "📋", action: "total",     tip: "Show all bookings"         },
    { label: "Upcoming",       value: upcomingConfirmedCount,                    icon: "📅", action: "upcoming",  tip: "Filter upcoming bookings"  },
    { label: "Completed",      value: completedCount,                            icon: "✅", action: "completed", tip: "Filter completed bookings" },
    { label: "Total Revenue",  value: `₹${totalRevenue.toLocaleString()}`,       icon: "💰", action: "revenue",  tip: "Online vs manual breakdown" },
  ];

  const ALL_TABS = [
    { id: "bookings",      label: "Bookings",      superOnly: false },
    { id: "courts",        label: "Courts",        superOnly: false },
    { id: "partner-codes", label: "Partner Codes", superOnly: false },
    { id: "block-dates",   label: "Block Dates",   superOnly: true  },
    { id: "rate-chart",    label: "Rate Chart",    superOnly: true  },
    { id: "promotions",    label: "Promotions",    superOnly: true  },
    { id: "webhook-logs", label: "Webhook Logs",  superOnly: true  },
    { id: "error-logs",   label: "Error Logs",    superOnly: true  },
  ];
  const TABS = ALL_TABS.filter((t) => isSuper || !t.superOnly);

  const applySearch = (list) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((b) => b.name?.toLowerCase().includes(q) || b.sport?.toLowerCase().includes(q));
  };

  const dateFiltered = (() => {
    // When a status filter is active, ignore the date pills and use its own logic
    if (statusFilter === "upcoming") {
      let base = bookings.filter((b) => b.status === "confirmed");
      if (upcomingSubFilter === "Today")      base = base.filter((b) => b.date === todayStr);
      else if (upcomingSubFilter === "This Week")  base = base.filter((b) => b.date >= todayStr && b.date <= nextWeekStr);
      else if (upcomingSubFilter === "This Month") base = base.filter((b) => b.date >= todayStr && b.date <= monthEndStr);
      else                                         base = base.filter((b) => b.date >= todayStr);
      return applySearch(base);
    }
    if (statusFilter === "completed") {
      return applySearch(bookings.filter((b) => b.status === "completed"));
    }
    // Default: existing date-pill logic
    const now = new Date();
    return applySearch(bookings.filter((b) => {
      const bDate = new Date(b.date + "T00:00:00");
      if (dateFilter === "Today")      return b.date === todayStr;
      if (dateFilter === "This Week") {
        const start = new Date(now);
        start.setDate(now.getDate() - now.getDay());
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return bDate >= start && bDate <= end;
      }
      if (dateFilter === "This Month") {
        return bDate.getMonth() === now.getMonth() && bDate.getFullYear() === now.getFullYear();
      }
      return true; // All Time
    }));
  })();

  const FAILURE_REASON_LABELS = {
    user_dismissed: "Customer cancelled",
    payment_failed: "Payment declined",
    expired:        "Timed out",
    razorpay_error: "Gateway error",
    browser_error:  "Technical error",
    slot_conflict:  "Slot taken",
  };

  const ACTIVE_STATUSES = ["upcoming", "confirmed", "pending"];
  const filteredByReason = failureReasonFilter
    ? dateFiltered.filter((b) => b.status === "failed" && b.failureReason === failureReasonFilter)
    : dateFiltered;

  const filtered = statusFilter === "upcoming"
    ? [...filteredByReason].sort((a, b) => (a.date <= b.date ? -1 : 1))
    : statusFilter === "completed"
    ? [...filteredByReason].sort((a, b) => (a.date >= b.date ? -1 : 1))
    : [...filteredByReason].sort((a, b) => {
        const aActive = ACTIVE_STATUSES.includes(a.status);
        const bActive = ACTIVE_STATUSES.includes(b.status);
        if (aActive && !bActive) return -1;
        if (!aActive && bActive) return 1;
        if (aActive) return a.date <= b.date ? -1 : 1;
        return a.date >= b.date ? -1 : 1;
      });

  const filteredRevenue = dateFiltered
    .filter((b) => ["confirmed", "completed", "upcoming"].includes(b.status))
    .reduce((s, b) => s + (b.total || 0), 0);

  const handleSavePricing = async (court, prices) => {
    try {
      await setDoc(doc(db, "settings", "pricing"), courtPrices);
      logAdminAction("update_price", `Updated prices for ${court} — weekday day: ₹${prices.weekdayDay}, weekday night: ₹${prices.weekdayNight}, weekend day: ₹${prices.weekendDay}, weekend night: ₹${prices.weekendNight}`);
      setToast("Prices saved");
    } catch (e) {
      console.error("Save pricing error:", e);
      setToast("Failed to save prices");
    }
  };

  const handleSavePromo = async () => {
    setPromoSaving(true);
    try {
      await setDoc(doc(db, "settings", "promotions"), {
        ...promoForm,
        minSlots: Number(promoForm.minSlots),
        value:    Number(promoForm.value),
        updatedAt: serverTimestamp(),
      });
      logAdminAction("update_promotion", `Promotion updated — type: ${promoForm.type}, active: ${promoForm.active}, minSlots: ${promoForm.minSlots}, value: ${promoForm.value}`);
      setToast("Promotion saved");
    } catch (e) {
      console.error("Save promo error:", e);
      setToast("Failed to save promotion");
    }
    setPromoSaving(false);
  };

  const handleAddBooking = async (booking) => {
    const bookingData = { ...booking, createdAt: serverTimestamp() };
    try {
      await setDoc(doc(db, "bookings", booking.id), bookingData);
      logAdminAction("add_booking", `Added booking ${booking.id} for ${booking.name} (${booking.sport}) on ${booking.date}`);
      setToast(`Booking added for ${booking.name}`);
    } catch (e) {
      console.error("Add booking error:", e);
      setToast("Failed to save booking");
    }
  };

  const exportCSV = () => {
    const headers = ["Booking ID", "Customer Name", "Phone", "Sport", "Date", "Time", "Amount", "Status", "Created At"];
    const rows = filtered.map((b) => {
      const sorted = [...(b.slots || [])].sort();
      const time = sorted.length
        ? `${formatTime12(sorted[0])} - ${formatTime12(sorted[sorted.length - 1])}`
        : "";
      const createdAt = b.createdAt?.seconds
        ? new Date(b.createdAt.seconds * 1000).toLocaleString("en-IN")
        : (b.createdAt || "");
      return [b.id, b.name, b.phone, b.sport, b.date, time, b.total ?? "", b.status, createdAt]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bookings-${dateFilter.toLowerCase().replace(/\s+/g, "-")}-${todayStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCancelConfirm = async (refundStatus, cancelNotes, onApiError) => {
    const { id, name, razorpay_payment_id, total } = cancelTarget;

    // ── "Process Refund Now" — call API first, only cancel if it succeeds ──
    if (refundStatus === "process_now") {
      try {
        const res = await fetch("/api/process-refund", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bookingId: id, razorpay_payment_id, amount: total }),
        });
        const data = await res.json();
        if (!data.ok) {
          onApiError(data.error || "Unknown refund error");
          return;
        }
        // Refund issued — fall through with refundStatus="refund_processed" so
        // Firebase is updated consistently (webhook will also set this, but race-safely)
        await cancelBooking(id, {
          refundStatus: "refund_processed",
          refundId:     data.refundId,
          refundAmount: data.amount,
          cancelNotes,
          cancelledAt: new Date().toISOString(),
        });
        logAdminAction("cancel_booking", `Cancelled ${id} for ${name} — API refund issued: ${data.refundId} ₹${data.amount}`);
        setToast(`Booking cancelled & ₹${data.amount?.toLocaleString()} refund issued`);
      } catch (e) {
        onApiError(e.message || "Network error");
        return;
      }
    } else {
      await cancelBooking(id, {
        refundStatus,
        cancelNotes,
        cancelledAt: new Date().toISOString(),
      });
      logAdminAction("cancel_booking", `Cancelled ${id} for ${name} — refund: ${refundStatus}${cancelNotes ? `, notes: ${cancelNotes}` : ""}`);
      setToast(`Booking cancelled for ${name}`);
    }

    setCancelTarget(null);
  };

  const sendReminder = (name) => {
    logAdminAction("send_reminder", `Sent reminder to ${name}`);
    setToast(`Reminder sent to ${name}`);
  };

  return (
    <div className="bg-white" style={{ paddingTop: 64 }}>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>

          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse inline-block" />
                <span className="text-black/40 text-xs font-bold uppercase tracking-[0.18em]">Admin Dashboard</span>
                {!isSuper && (
                  <span className="ml-1 text-[10px] font-black px-2 py-0.5 rounded-full bg-black/5 border border-black/10 text-black/40 uppercase tracking-wider">
                    View Only Access
                  </span>
                )}
              </div>
              <h1
                className="text-black font-black text-3xl sm:text-4xl tracking-tight mb-1"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Ground Management
              </h1>
              <p className="text-black/40 text-sm">Town Square Arena · Hyderabad</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex-shrink-0 flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-xl border border-black/10 text-black/40 hover:text-black hover:border-black/25 hover:bg-black/[0.03] transition-all mt-1"
            >
              <span>↩</span> Logout
            </button>
          </div>

          {/* ── Overall stat cards ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {stats.map((s) => {
              const isActive = (s.action === "upcoming" && statusFilter === "upcoming")
                || (s.action === "completed" && statusFilter === "completed")
                || (s.action === "revenue" && showRevTooltip);
              return (
                <div key={s.label} className="relative">
                  <button
                    type="button"
                    title={s.tip}
                    onClick={() => handleStatCard(s.action)}
                    className={`w-full text-left rounded-2xl border p-4 transition-all cursor-pointer group ${
                      isActive
                        ? "border-[#f59e0b] bg-amber-50 shadow-sm"
                        : "border-black/8 hover:border-black/20 hover:shadow-sm active:scale-[0.98]"
                    }`}
                    style={{ background: isActive ? undefined : "#f9fafb" }}
                  >
                    <p className="text-black/40 text-xs font-semibold mb-2 flex items-center justify-between">
                      <span>{s.icon} {s.label}</span>
                      <span className="text-[9px] text-black/20 group-hover:text-black/40 transition-colors hidden sm:block">↗</span>
                    </p>
                    <p
                      className="text-black font-bold text-2xl leading-none"
                      style={{ fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      {s.value}
                    </p>
                  </button>
                  {/* Revenue breakdown tooltip */}
                  {s.action === "revenue" && showRevTooltip && (
                    <div className="absolute top-full left-0 mt-2 z-20 bg-white border border-black/10 rounded-2xl shadow-xl p-4 w-56">
                      <p className="text-[10px] font-black text-black/40 uppercase tracking-[0.15em] mb-3">Revenue Breakdown</p>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-black/50 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />
                            Online
                          </span>
                          <span className="text-sm font-bold text-black">₹{onlineRevenue.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-black/50 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />
                            Manual
                          </span>
                          <span className="text-sm font-bold text-black">₹{manualRevenue.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-black/6">
                          <span className="text-xs font-bold text-black/50">Total</span>
                          <span className="text-sm font-black text-black">₹{totalRevenue.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Revenue chart ── */}
          <RevenueChart bookings={bookings} />

          {/* ── Tabs ── */}
          <div className="flex gap-1 p-1 rounded-xl border border-black/8 w-fit mb-6" style={{ background: "#f9fafb" }}>
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                  tab === t.id ? "bg-[#f59e0b] text-black shadow-sm" : "text-black/40 hover:text-black"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ── Partner Codes Tab ── */}
          {tab === "partner-codes" && (
            <PartnerCodesTab bookings={bookings} />
          )}

          {/* ── Bookings Tab ── */}
          {tab === "bookings" && (
            <div ref={bookingsSectionRef}>
              {/* Active status filter pill */}
              {statusFilter && (
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold">
                    <span>
                      {statusFilter === "upcoming"
                        ? `Upcoming Bookings · ${upcomingSubFilter} (${filtered.length})`
                        : `Completed Bookings (${filtered.length})`}
                    </span>
                    <button
                      type="button"
                      onClick={() => setStatusFilter(null)}
                      className="text-amber-400 hover:text-amber-700 font-black leading-none ml-1"
                      title="Clear filter"
                    >
                      ×
                    </button>
                  </span>
                </div>
              )}

              {/* Upcoming sub-filter pills */}
              {statusFilter === "upcoming" && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {["Today", "This Week", "This Month", "All Upcoming"].map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setUpcomingSubFilter(f)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                        upcomingSubFilter === f
                          ? "bg-black text-white border-black"
                          : "bg-white text-black/50 border-black/10 hover:border-black/30 hover:text-black"
                      }`}
                    >
                      {f} ({upcomingCounts[f]})
                    </button>
                  ))}
                </div>
              )}

              {/* Date filter + revenue + export row */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                {/* Hide date pills when a status filter overrides them */}
                {!statusFilter && (
                  <div className="flex items-center gap-1 p-1 rounded-xl border border-black/8 w-fit" style={{ background: "#f9fafb" }}>
                    {["Today", "This Week", "This Month", "All Time"].map((f) => (
                      <button
                        key={f}
                        onClick={() => setDateFilter(f)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                          dateFilter === f ? "bg-[#f59e0b] text-black shadow-sm" : "text-black/40 hover:text-black"
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 sm:ml-auto flex-shrink-0">
                  <div className="text-sm font-bold text-black/50">
                    Revenue: <span className="text-black">₹{filteredRevenue.toLocaleString()}</span>
                  </div>
                  <button
                    onClick={exportCSV}
                    disabled={filtered.length === 0}
                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl border border-black/10 text-black/50 hover:text-black hover:border-black/20 hover:bg-black/[0.03] disabled:opacity-30 disabled:cursor-not-allowed transition-all whitespace-nowrap"
                  >
                    ↓ Export CSV
                  </button>
                </div>
              </div>

              {/* Search + Add */}
              <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/30 text-sm">🔍</span>
                  <input
                    type="text"
                    placeholder="Search by name or sport..."
                    className="w-full border border-black/8 rounded-xl pl-9 pr-4 py-2.5 text-sm text-black bg-black/[0.02] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 focus:border-[#f59e0b]/50 transition-all placeholder-black/25"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <select
                  value={failureReasonFilter}
                  onChange={(e) => setFailureReasonFilter(e.target.value)}
                  className="border border-black/8 rounded-xl px-3 py-2.5 text-xs font-bold text-black/50 bg-black/[0.02] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 transition-all cursor-pointer"
                  title="Filter by failure reason"
                >
                  <option value="">All Failures</option>
                  {Object.entries(FAILURE_REASON_LABELS).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
                {isSuper && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 bg-[#f59e0b] hover:bg-amber-400 text-black font-black text-sm px-4 py-2.5 rounded-xl transition-all hover:shadow-lg hover:shadow-[#f59e0b]/20 whitespace-nowrap"
                  >
                    + Add Booking
                  </button>
                )}
              </div>

              {filtered.length === 0 ? (
                <div className="text-center py-16 border border-black/6 rounded-2xl" style={{ background: "#f9fafb" }}>
                  <p className="text-2xl mb-2">📭</p>
                  <p className="text-black font-bold text-sm mb-1">No bookings found</p>
                  <p className="text-black/35 text-xs">
                    {search ? `No results for "${search}" in ${dateFilter}` : `No bookings for ${dateFilter}`}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filtered.map((b, i) => {
                    // Combine regular + next-day slots, sort by absolute hour position
                    const allSlots = [
                      ...(b.slots || (b.slot ? [b.slot] : [])),
                      ...(b.nextDaySlots || []),
                    ].sort((a, z) => slotHour(a) - slotHour(z));
                    const hasNextDay = (b.nextDaySlots || []).length > 0;
                    const dateLabel = b.date
                      ? new Date(b.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })
                      : "—";
                    const nextDayLabel = (() => {
                      if (!hasNextDay || !b.date) return "";
                      const d = new Date(b.date + "T00:00:00");
                      d.setDate(d.getDate() + 1);
                      return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
                    })();
                    const st = STATUS_STYLES[b.status] || STATUS_STYLES.upcoming;
                    return (
                      <motion.div
                        key={b.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className="bg-white border border-black/8 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-4"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-black font-bold text-sm">{b.name || b.id}</span>
                            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wide ${st.badge}`}>
                              {b.status}
                            </span>
                            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wide border ${
                              b.source === "manual"
                                ? "bg-slate-50 text-slate-500 border-slate-200"
                                : "bg-blue-50 text-blue-500 border-blue-100"
                            }`}>
                              {b.source === "manual" ? "Manual" : "Online"}
                            </span>
                            {b.status === "cancelled" && (() => {
                              const rs = b.refundStatus;
                              if (!rs) return null;
                              const isProcessed = rs === "refund_processed";
                              const isPending   = rs === "refund_pending";
                              const isNone      = rs === "no_refund";
                              return (
                                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wide border ${
                                  isProcessed ? "bg-green-50 text-green-600 border-green-100"
                                  : isNone    ? "bg-black/5 text-black/40 border-black/8"
                                  :             "bg-amber-50 text-amber-700 border-amber-200"
                                }`}>
                                  {isProcessed
                                    ? b.refundId
                                      ? `Refunded ₹${(b.refundAmount ?? b.total)?.toLocaleString()}`
                                      : "Refund Processed"
                                    : isNone
                                    ? "No Refund"
                                    : "⚠ Refund Pending"}
                                </span>
                              );
                            })()}
                          </div>
                          <p className="text-black/50 text-xs mb-0.5">
                            {b.sport}{b.courtNumber && b.sport !== "Oval Cricket" ? ` · Court ${b.courtNumber}` : ""} · {dateLabel}
                            {allSlots.length > 0 && (
                              <>
                                {" · "}{formatTime12(allSlots[0])} → {formatTime12(slotEndTime(allSlots[allSlots.length - 1]))}
                                {hasNextDay && <span className="text-black/35"> ({nextDayLabel})</span>}
                              </>
                            )}
                          </p>
                          <p className="text-black/35 text-xs">
                            {b.id} · {b.phone}
                            {b.refundId && <span className="ml-1 text-black/25 font-mono">{b.refundId}</span>}
                          </p>
                          {b.userAgent && (() => {
                            const d = parseDevice(b.userAgent);
                            return d ? (
                              <p className="text-black/25 text-[10px] mt-0.5 flex items-center gap-1.5 flex-wrap">
                                <span>{d.deviceType === "Mobile" ? "📱" : d.deviceType === "Tablet" ? "📱" : "🖥️"}</span>
                                <span>{d.os}</span>
                                <span className="text-black/15">·</span>
                                <span>{d.browser}</span>
                                <span className="text-black/15">·</span>
                                <span>{d.deviceType}</span>
                                {b.viewport && <><span className="text-black/15">·</span><span>{b.viewport}</span></>}
                              </p>
                            ) : null;
                          })()}
                          {b.cancelNotes && (
                            <p className="text-black/35 text-xs mt-0.5 italic">"{b.cancelNotes}"</p>
                          )}
                          {b.status === "failed" && b.failureReason && (
                            <p className="text-[10px] mt-1 flex items-center gap-1.5 flex-wrap">
                              <span className="bg-black/5 border border-black/8 text-black/40 font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                                {FAILURE_REASON_LABELS[b.failureReason] || b.failureReason}
                              </span>
                              {b.errorMessage && (
                                <span className="text-black/30 italic">{b.errorMessage}</span>
                              )}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span
                            className="font-bold text-lg"
                            style={{ fontFamily: "Inter, system-ui, sans-serif", color: "#f59e0b" }}
                          >
                            ₹{b.total?.toLocaleString() ?? "—"}
                          </span>
                          {isSuper && ACTIVE_STATUSES.includes(b.status) && (
                            <button
                              onClick={() => setCancelTarget(b)}
                              className="text-xs font-bold px-3 py-1.5 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 hover:border-red-300 transition-all whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          )}
                          <button
                            onClick={() => sendReminder(b.name || b.id)}
                            className="text-xs font-bold px-3 py-1.5 rounded-xl border border-black/8 text-black/50 hover:text-black hover:border-black/20 hover:bg-black/[0.03] transition-all whitespace-nowrap"
                          >
                            📱 Remind
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Courts Tab ── */}
          {tab === "courts" && <CourtsTab bookings={bookings} />}

          {/* ── Block Dates Tab ── */}
          {tab === "block-dates" && (
            <BlockDatesTab setToast={setToast} />
          )}

          {/* ── Rate Chart Tab ── */}
          {tab === "rate-chart" && (
            <div>
              <p className="text-xs font-black text-black/40 uppercase tracking-[0.18em] mb-4">Per hour slot</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(courtPrices).filter(([court]) => court !== "Pickleball Court 3").map(([court, prices]) => (
                  <CourtPriceCard
                    key={court}
                    court={court}
                    prices={prices}
                    onChange={(field, val) =>
                      setCourtPrices((prev) => ({ ...prev, [court]: { ...prev[court], [field]: val } }))
                    }
                    onSave={() => handleSavePricing(court, prices)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Promotions Tab ── */}
          {tab === "promotions" && (
            <div className="space-y-5">
              <div className="bg-white border border-black/8 rounded-2xl p-5">
                <p className="text-xs font-black text-black/40 uppercase tracking-[0.18em] mb-4">Promotion Settings</p>

                {/* Active toggle */}
                <div className="flex items-center justify-between py-3 px-4 rounded-xl border border-black/6 mb-4" style={{ background: "#f9fafb" }}>
                  <div>
                    <p className="text-black font-bold text-sm">Promotion Active</p>
                    <p className="text-black/40 text-xs mt-0.5">Banner and discounts are shown to users when on</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPromoForm((f) => ({ ...f, active: !f.active }))}
                    className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${promoForm.active ? "bg-green-500" : "bg-black/20"}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${promoForm.active ? "right-0.5" : "left-0.5"}`} />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Banner text */}
                  <div>
                    <label className="block text-black font-bold text-xs mb-1.5">Banner Text</label>
                    <textarea
                      rows={2}
                      className="w-full border border-black/10 rounded-xl px-4 py-3 text-sm text-black bg-black/[0.02] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 focus:border-[#f59e0b]/50 transition-all resize-none"
                      value={promoForm.bannerText}
                      onChange={(e) => setPromoForm((f) => ({ ...f, bannerText: e.target.value }))}
                      placeholder="Enter promotional offer text..."
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Minimum slots */}
                    <div>
                      <label className="block text-black font-bold text-xs mb-1.5">Minimum Slots to Qualify</label>
                      <input
                        type="number"
                        min="1"
                        className="w-full border border-black/10 rounded-xl px-3 py-2.5 text-sm text-black bg-black/[0.02] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 transition-all"
                        value={promoForm.minSlots}
                        onChange={(e) => setPromoForm((f) => ({ ...f, minSlots: e.target.value }))}
                      />
                    </div>

                    {/* Promotion type */}
                    <div>
                      <label className="block text-black font-bold text-xs mb-1.5">Promotion Type</label>
                      <select
                        className="w-full border border-black/10 rounded-xl px-3 py-2.5 text-sm text-black bg-black/[0.02] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 transition-all"
                        value={promoForm.type}
                        onChange={(e) => setPromoForm((f) => ({ ...f, type: e.target.value }))}
                      >
                        {PROMO_TYPES.map((t) => (
                          <option key={t.id} value={t.id}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Value — label changes by type */}
                  <div>
                    <label className="block text-black font-bold text-xs mb-1.5">{VALUE_LABELS[promoForm.type]}</label>
                    <input
                      type="number"
                      min="0"
                      className="w-full border border-black/10 rounded-xl px-3 py-2.5 text-sm text-black bg-black/[0.02] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 transition-all"
                      value={promoForm.value}
                      onChange={(e) => setPromoForm((f) => ({ ...f, value: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Live preview */}
                <div className="mt-5">
                  <p className="text-black/40 text-xs font-semibold mb-2">Preview</p>
                  {promoForm.active ? (
                    <div className="bg-[#f59e0b]/10 border border-[#f59e0b]/20 rounded-xl py-2.5 px-4">
                      <p className="text-center text-[#b45309] text-sm font-medium">
                        🎁 <span className="text-[#f59e0b] font-black">Promotional Offer:</span> {promoForm.bannerText || "…"}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-xl py-2.5 px-4 border border-black/6" style={{ background: "#f9fafb" }}>
                      <p className="text-center text-black/30 text-sm">Banner hidden — promotion is inactive</p>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleSavePromo}
                  disabled={promoSaving}
                  className="mt-5 bg-[#f59e0b] hover:bg-amber-400 disabled:opacity-40 text-black font-black text-sm px-6 py-2.5 rounded-xl transition-all"
                >
                  {promoSaving ? "Saving..." : "Save & Publish"}
                </button>
              </div>
            </div>
          )}

          {/* ── Webhook Logs Tab ── */}
          {tab === "webhook-logs" && <WebhookLogsTab />}
          {tab === "error-logs"   && <ErrorLogsTab />}

        </motion.div>
      </div>

      <AnimatePresence>
        {showAddModal && (
          <AddBookingModal onClose={() => setShowAddModal(false)} onSave={handleAddBooking} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {cancelTarget && (
          <CancelModal
            booking={cancelTarget}
            onClose={() => setCancelTarget(null)}
            onConfirm={handleCancelConfirm}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && <Toast message={toast} onDone={() => setToast(null)} />}
      </AnimatePresence>
    </div>
  );
}
